import React, { useState, useEffect, Suspense } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThoughtInput } from './components/neural/ThoughtInput.tsx';
import { QuantumHologram } from './components/quantum/QuantumHologram.tsx';
import { QuantumConsciousness } from './components/quantum/QuantumConsciousness.tsx';
import { QuantumInsights } from './components/quantum/QuantumInsights.tsx';
import { QuantumThoughtVisualizer } from './components/quantum/QuantumThoughtVisualizer.tsx';
import { NeuralResonanceChamber } from './components/quantum/NeuralResonanceChamber.tsx';
import { QuantumAudioVisualizer } from './components/quantum/QuantumAudioVisualizer.tsx';
import { NeuralResonanceMatrix } from './components/neural/NeuralResonanceMatrix.tsx';
import { QuantumChatHistory } from './components/quantum/QuantumChatHistory.tsx';
import { QuantumConsciousnessPortal } from './components/quantum/QuantumConsciousnessPortal.tsx';
import { QuantumConsciousnessStream } from './components/quantum/QuantumConsciousnessStream.tsx';
import { QuantumConsciousnessManifest } from './components/quantum/QuantumConsciousnessManifest.tsx';
import { QuantumShaderDisplay } from './components/quantum/QuantumShader.tsx';




// Fix lazy imports - remove .tsx extension and export default properly
const BrainwaveVisualizer = React.lazy(() => import('./components/neural/BrainwaveVisualizer.tsx')
  .then(module => ({ default: module.BrainwaveVisualizer }))
);


const NeuralNetwork3D = React.lazy(() => import('./components/neural/NeuralNetwork3D.tsx')
  .then(module => ({ default: module.NeuralNetwork3D }))
);


const NeuraContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  padding: 2rem;
  padding-top: 80px;
  position: relative;
  overflow-x: hidden;
`;

const NeuralGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 255, 136, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 255, 136, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.3;
`;

const FloatingOrbs = styled(motion.div)`
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(0, 255, 136, 0.2), transparent);
  filter: blur(40px);
`;

const MainContent = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  padding: 60px 1rem 0;
  pointer-events: auto;
`;

const Title = styled(motion.h1)`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #00ff88;
`;

const StatusBar = styled(motion.div)`
  position: fixed;
  top: 60px;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  z-index: 1000;
`;

const StatusIndicator = styled(motion.div)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00ff88;
`;

const StatusText = styled.span`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const FuturisticQuote = styled(motion.div)`
  text-align: center;
  font-style: italic;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
  margin: 0 auto 2rem;
  padding: 0.5rem;
  position: relative;
`;

const StatsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  h3 {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 1.5rem;
    color: #00ff88;
    margin: 0;
  }
`;

const QuantumSection = styled(motion.div)`
  margin: 2rem 0;
  position: relative;
`;

const QuantumTitle = styled.h2`
  font-size: 1.75rem;
  color: #00ff88;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ResonanceSection = styled(motion.div)`
  margin: 2rem 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 1px;
    height: 20px;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(0, 255, 136, 0.5)
    );
  }
`;

const TimeDisplay = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  left: 20px;
  font-family: monospace;
  color: rgba(0, 255, 136, 0.8);
  font-size: 0.9rem;
  line-height: 1.5;
  background: rgba(0, 0, 0, 0.3);
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid rgba(0, 255, 136, 0.2);
  z-index: 1000;
`;

const NavBar = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  z-index: 2000;
  border-bottom: 1px solid rgba(0, 255, 136, 0.3);
  pointer-events: all;
`;

const NavLink = styled(motion.button)`
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 1rem;
  letter-spacing: 1px;
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  background: rgba(0, 255, 136, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.1);
  outline: none;
  position: relative;
  z-index: 2001;
  pointer-events: auto;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.3);

  &:hover {
    color: #00ff88;
    background: rgba(0, 255, 136, 0.15);
    border-color: rgba(0, 255, 136, 0.3);
    transform: translateY(-1px);
    box-shadow: 0 0 15px rgba(0, 255, 136, 0.2);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const QuantumMemorySection = styled(QuantumSection)`
  margin-bottom: 4rem;  // Add extra margin at bottom
`;

const MemoryMetrics = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 15px;
  border: 1px solid rgba(0, 255, 136, 0.1);
`;

const MemoryMetric = styled(motion.div)`
  text-align: center;
  padding: 1rem;
  
  h4 {
    color: rgba(0, 255, 136, 0.8);
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
  }
`;

const QuoteNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
`;

const QuoteButton = styled.button<{ disabled?: boolean }>`
  background: transparent;
  border: 1px solid ${props => props.disabled ? '#444' : '#00ff88'};
  color: ${props => props.disabled ? '#444' : '#00ff88'};
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 255, 136, 0.1);
  }
`;

export const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [quotes, setQuotes] = useState<string[]>([]);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [tempQuote, setTempQuote] = useState('');

  const fetchQuote = async () => {
    setIsLoadingQuote(true);
    try {
      const response = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY!
        },
        body: JSON.stringify({
          provider: 'phi',
          messages: [
            { role: "system", content: "You are QUANTUM-2100, an advanced AI system from the year 2100. Generate profound, futuristic quotes." },
            { role: "user", content: "Generate one profound quote about the future..." }
          ],
          temperature: 0.7,
          max_tokens: 800,
          top_p: 0.95,
          frequency_penalty: 0,
          presence_penalty: 0,
          stop: null
        })
      });

      if (!response.ok) throw new Error(`Quantum core error (${response.status})`);
      
      const reader = response.body?.getReader();
      let accumulatedQuote = '';
      
      while (true) {
        const { done, value } = await reader!.read();
        if (done) break;
        
        const chunk = new TextDecoder().decode(value);
        const jsonLines = chunk.split('\n').filter(line => line.trim());
        
        for (const line of jsonLines) {
          try {
            const cleanedLine = line.replace(/^(data:\s*)+/, '');
            if (cleanedLine === '[DONE]' || !cleanedLine.trim()) continue;

            const jsonData = JSON.parse(cleanedLine);
            const content = jsonData.content ||
                           jsonData.choices?.[0]?.delta?.content || 
                           jsonData.choices?.[0]?.content || 
                           '';
            
            if (content) {
              accumulatedQuote += content;
              setTempQuote(accumulatedQuote.replace(/\s+/g, ' ').trim());
            }
          } catch (e) {
            console.debug('Skipping invalid JSON line:', line);
            continue;
          }
        }
      }

      if (accumulatedQuote.trim()) {
        const cleanQuote = accumulatedQuote.replace(/\s+/g, ' ').trim();
        setQuotes(prev => [...prev, cleanQuote]);
        if (quotes.length > 0) {
          setCurrentQuoteIndex(quotes.length);
        }
      }

    } catch (error) {
      console.log('Error fetching quote:', error);
    } finally {
      setIsLoadingQuote(false);
      setTempQuote('');
    }
  };

  useEffect(() => {
    fetchQuote();
  }, []);

  // Add auto-rotation effect
  useEffect(() => {
    const rotationInterval = setInterval(() => {
      if (!isLoadingQuote) {
        if (currentQuoteIndex < quotes.length - 1) {
          setCurrentQuoteIndex(prev => prev + 1);
        } else {
          fetchQuote();
        }
      }
    }, 10000); // Rotate every 10 seconds

    return () => clearInterval(rotationInterval);
  }, [currentQuoteIndex, quotes.length, isLoadingQuote]);

  // Cleanup on unmount
  useEffect(() => {
    setIsVisible(true);
    return () => {
      setIsVisible(false);
      // Cleanup WebGL contexts
      const event = new CustomEvent('cleanup-quantum-visualizers');
      window.dispatchEvent(event);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <NeuraContainer>
      <NavBar
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <NavLink onClick={() => scrollToSection('neural-interface')}>
          Neural Interface
        </NavLink>
        <NavLink onClick={() => scrollToSection('quantum-hologram')}>
          Quantum Hologram
        </NavLink>
        <NavLink onClick={() => scrollToSection('resonance-chamber')}>
          Resonance Chamber
        </NavLink>
        <NavLink onClick={() => scrollToSection('consciousness-portal')}>
          Consciousness Portal
        </NavLink>
      </NavBar>
      <NeuralGrid />
      <FloatingOrbs
        initial={{ x: -100, y: -100 }}
        animate={{ 
          x: [-100, window.innerWidth - 200, -100],
          y: [-100, window.innerHeight - 200, -100]
        }}
        transition={{ 
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <StatusBar
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <StatusIndicator
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <StatusText>Neural Link Active</StatusText>
      </StatusBar>
      
      <MainContent>
        <Title
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          QUANTUM-2100
        </Title>
        
        <FuturisticQuote
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <motion.span
            key={currentQuoteIndex}
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: isLoadingQuote ? [0.3, 1, 0.3] : 1,
            }}
            transition={isLoadingQuote ? {
              duration: 2,
              repeat: Infinity,
              ease: "linear"
            } : {
              duration: 0.5
            }}
          >
            "{isLoadingQuote ? tempQuote : quotes[currentQuoteIndex] || ''}"
          </motion.span>
        </FuturisticQuote>
        
        <div id="neural-interface">
          <ThoughtInput />
          {/* Only load heavy components when visible */}
          {isVisible && (
            <>
              <QuantumThoughtVisualizer />
              <StatsGrid
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <StatCard whileHover={{ scale: 1.05 }}>
                  <h3>Neural Bandwidth</h3>
                  <p>1.2 TB/s</p>
                </StatCard>
                <StatCard whileHover={{ scale: 1.05 }}>
                  <h3>Thought Latency</h3>
                  <p>0.23ms</p>
                </StatCard>
                <StatCard whileHover={{ scale: 1.05 }}>
                  <h3>Sync Rate</h3>
                  <p>99.9%</p>
                </StatCard>
                <StatCard whileHover={{ scale: 1.05 }}>
                  <h3>Neural Load</h3>
                  <p>42%</p>
                </StatCard>
              </StatsGrid>

              <div className="middle-section">
                <Suspense fallback={<div>Loading Neural Network...</div>}>
                  <NeuralNetwork3D />
                </Suspense>
                <NeuralResonanceMatrix />
                <Suspense fallback={<div>Loading Brainwaves...</div>}>
                  <BrainwaveVisualizer />
                </Suspense>
              </div>

              <QuantumAudioVisualizer 
                isProcessing={false}
                aiMessage="Quantum neural interface initialized. Ready for consciousness transfer."
              />

              {/* <QuantumInsights /> */}
              
            </>
          )}
        </div>

        <QuantumSection id="quantum-hologram">
          <QuantumTitle>Quantum Holographic Interface</QuantumTitle>
          <QuantumHologram />
        </QuantumSection>

        <ResonanceSection id="resonance-chamber">
          <QuantumTitle>Neural Resonance Chamber</QuantumTitle>
          <NeuralResonanceChamber />
        </ResonanceSection>

        <QuantumSection id="consciousness-portal">
          <QuantumTitle>Quantum Consciousness Portal</QuantumTitle>
          <QuantumConsciousnessPortal />
        </QuantumSection>

        <QuantumSection id="consciousness-manifest">
          <QuantumTitle>Quantum Consciousness Manifestation</QuantumTitle>
          <QuantumConsciousnessManifest />
        </QuantumSection>

        { <TimeDisplay
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 2 }}
        >
          QUANTUM TIME: {new Date().getFullYear() + 78} 
          <br />
          TEMPORAL STABILITY: 99.99%
          <br />
          TIMELINE: ALPHA-PRIME
        </TimeDisplay> }

        {/* <QuantumConsciousnessStream /> */}

        {/* <QuantumShaderDisplay /> */}

        <QuantumChatHistory />
      </MainContent>
    </NeuraContainer>
  );
}; 